import {
  Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';

export type ActionButtonEvent = 'edit' | 'delete' | 'dataReview' | 'clipboard' | 'export';
export type PopoverMenuItem = {
  id: ActionButtonEvent;
  label: string;
  icon: string;
  color: string;
  isDisabled?: boolean;
  showConfirmDialog?: boolean;
};

export type DynamicActionState = { isDisabled?: boolean };
export type DynamicActionStates = { [key in ActionButtonEvent]?: DynamicActionState };

const MENU_BUTTONS_CONFIG: { [key in ActionButtonEvent]: PopoverMenuItem } = {
  clipboard: {
    color: 'primary',
    icon: 'copy-outline',
    id: 'clipboard',
    label: 'BUTTON.COPY_TO_CLIPBOARD',
  },
  dataReview: {
    color: 'primary',
    icon: 'clipboard-outline',
    id: 'dataReview',
    // isDisabled is set dynamically via @Input() dynamicActionStates
    label: 'BUTTON.REQUEST_DATA_REVIEW',
    showConfirmDialog: true,
  },
  delete: {
    color: 'danger',
    icon: 'trash-outline',
    id: 'delete',
    label: 'BUTTON.DELETE',
    showConfirmDialog: true,
  },
  edit: {
    color: 'primary',
    icon: 'create-outline',
    id: 'edit',
    label: 'BUTTON.EDIT',
  },
  export: {
    color: 'primary',
    icon: 'download-outline',
    id: 'export',
    label: 'BUTTON.EXPORT',
  },
};

@Component({
  selector: 'proto-action-button-group',
  standalone: false,
  styleUrls: ['./action-button-group.component.scss'],
  templateUrl: './action-button-group.component.html',
})
export class ActionButtonGroupComponent implements OnChanges, OnInit {
  @ViewChild('popover') popover!: HTMLIonPopoverElement;

  @Input() isLoading: boolean;
  @Input() menuIsDisabled: boolean = false;
  @Input() isAuthorized: boolean = true;
  @Input() availableActions: ActionButtonEvent[] = ['edit', 'delete']; // by default edit and delete are available

  @Output() buttonClicked: EventEmitter<ActionButtonEvent> = new EventEmitter<ActionButtonEvent>();

  public menuIsActive = false;
  public menuItems: PopoverMenuItem[] = [];
  public actionFlags: { [key in ActionButtonEvent]?: boolean } = {}; // used to quickly check if action is available

  private _dynamicActionStates: DynamicActionStates = {};

  get dynamicActionStates(): DynamicActionStates {
    return this._dynamicActionStates;
  }

  @Input() set dynamicActionStates(states: DynamicActionStates) {
    this._dynamicActionStates = states;
    this.menuItems = this.getPopoverMenuItems();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableActions) {
      this.syncActions();
    }
  }

  public ngOnInit(): void {
    this.syncActions();
  }

  public showMenu(e: Event): void {
    this.popover.event = e;
    this.menuIsActive = true;
  }

  public onMenuItemClicked(action: ActionButtonEvent): void {
    this.menuIsActive = false;

    requestAnimationFrame(() => {
      this.buttonClicked.emit(action);
    });
  }

  private syncActions(): void {
    this.menuItems = this.getPopoverMenuItems();
    this.actionFlags = this.availableActions.reduce((flags, action) => {
      flags[action] = true;

      return flags;
    }, {} as { [key in ActionButtonEvent]?: boolean });
  }

  private getPopoverMenuItems(): PopoverMenuItem[] {
    return this.availableActions.map((action) => {
      const staticConfig = MENU_BUTTONS_CONFIG[action];
      const dynamicState = this.dynamicActionStates?.[action] || {};

      return {
        ...staticConfig,
        ...dynamicState,
      };
    });
  }
}
